import * as styles from "./DataTableFilter.css";

import { type JSX, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Checkbox } from "@frontend/components/atoms/Checkbox";
import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@frontend/components/atoms/Popover";
import {
  ScrollArea,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { useLingui } from "@lingui/react/macro";

import type {
  DataTableColumnFilter,
  DataTableColumnFilterSpec,
  DataTableFilterOperator,
} from "./DataTable.types";
import { FILTER_OPERATORS } from "./constants";

interface DataTableFilterProps {
  columnId: string;
  label: string;
  spec: DataTableColumnFilterSpec;
  filter?: DataTableColumnFilter;
  onChange: (filter: DataTableColumnFilter | null) => void;
}

export function DataTableFilter({
  columnId,
  label,
  spec,
  filter,
  onChange,
}: DataTableFilterProps): JSX.Element {
  const { t } = useLingui();

  const locale = useIntlLocale();

  const options =
    typeof spec === "object" && "select" === spec.type ? spec.options : null;

  const picker =
    typeof spec === "object" && "picker" === spec.type ? spec.render : null;

  const type = typeof spec === "object" ? "text" : spec;

  const labels: Record<string, Record<string, string>> = {
    text: {
      contains: t`Contains`,
      equals: t`Is equal to`,
      startsWith: t`Starts with`,
    },
    number: {
      eq: t`Is equal to`,
      ne: t`Is not equal to`,
      gt: t`Is greater than`,
      lt: t`Is less than`,
      between: t`Is between`,
    },
    date: {
      eq: t`On`,
      gt: t`After`,
      lt: t`Before`,
      between: t`Between`,
    },
    dateRange: {
      between: t`Between`,
    },
  };

  const operators = FILTER_OPERATORS[type].map((value) => ({
    value,
    label: labels[type][value],
  }));

  const [operator, setOperator] = useState<DataTableFilterOperator>(
    filter?.operator ?? operators[0].value,
  );
  const [value, setValue] = useState(filter?.value ?? "");
  const [value2, setValue2] = useState(filter?.value2 ?? "");
  const [values, setValues] = useState<string[]>(filter?.values ?? []);

  const isRange = operator === "between";

  const choosesOperator = 1 < operators.length;

  const canApply =
    options || picker
      ? values.length > 0
      : value !== "" && (!isRange || value2 !== "");

  const apply = () => {
    if (!canApply) {
      return;
    }

    onChange(
      options || picker
        ? { id: columnId, operator: "in", values }
        : { id: columnId, operator, value, ...(isRange ? { value2 } : {}) },
    );
  };

  const clear = () => {
    setValue("");
    setValue2("");
    setValues([]);
    onChange(null);
  };

  const toggle = (option: string, checked: boolean) => {
    setValues((previous) =>
      checked
        ? [...previous, option]
        : previous.filter((entry) => entry !== option),
    );
  };

  const renderValue = (
    current: string,
    setCurrent: (next: string) => void,
    placeholder: string,
  ) =>
    type === "date" ? (
      <DatePicker
        locale={locale}
        value={current || null}
        onValueChange={(next) => setCurrent(next ?? "")}
      >
        <DatePickerTrigger>
          <DatePickerValue />
        </DatePickerTrigger>
        <DatePickerContent />
      </DatePicker>
    ) : (
      <Input
        size="sm"
        clearable
        value={current}
        onValueChange={setCurrent}
        placeholder={placeholder}
        inputMode={type === "number" ? "decimal" : undefined}
      />
    );

  const rangePicker = (
    <DatePicker
      range
      locale={locale}
      value={[value || null, value2 || null]}
      onValueChange={([start, end]) => {
        setValue(start ?? "");
        setValue2(end ?? "");
      }}
    >
      <DatePickerTrigger>
        <DatePickerValue />
      </DatePickerTrigger>
      <DatePickerContent />
    </DatePicker>
  );

  return (
    <Popover>
      <PopoverTrigger
        render={
          <button
            type="button"
            data-slot="data-table-filter"
            className={styles.trigger({ active: Boolean(filter) })}
            aria-label={t`Filter ${label}`}
          >
            <Icon name={filter ? "Funnel" : "ListFilter"} size="sm" />
          </button>
        }
      />

      <PopoverContent align="start" className={styles.panel}>
        <PopoverTitle>{label}</PopoverTitle>

        {options ? (
          <ScrollArea>
            <ScrollAreaViewport className={styles.optionsViewport}>
              <div className={styles.options}>
                {options.map((option) => (
                  <Checkbox
                    key={option.value}
                    size="sm"
                    label={option.label}
                    checked={values.includes(option.value)}
                    onCheckedChange={(checked) => toggle(option.value, checked)}
                  />
                ))}
              </div>
            </ScrollAreaViewport>

            <ScrollAreaScrollbar>
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
          </ScrollArea>
        ) : null}

        {picker
          ? picker({
              value: values[0] ?? null,
              onValueChange: (next) => setValues(null === next ? [] : [next]),
            })
          : null}

        {options || picker || !choosesOperator ? null : (
          <Select<DataTableFilterOperator>
            items={operators}
            value={operator}
            onValueChange={(next) => next && setOperator(next)}
            size="sm"
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              {operators.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {options || picker ? null : "dateRange" === type ? (
          rangePicker
        ) : (
          <>
            {renderValue(value, setValue, t`Value`)}
            {isRange ? renderValue(value2, setValue2, t`To`) : null}
          </>
        )}

        <div className={styles.actions}>
          <PopoverClose
            render={
              <Button variant="ghost" size="sm" onClick={clear}>
                {t`Clear`}
              </Button>
            }
          />

          <PopoverClose
            render={
              <Button size="sm" disabled={!canApply} onClick={apply}>
                {t`Apply`}
              </Button>
            }
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
